
import { useState, useEffect, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuTrigger, NavigationMenuContent, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

// Predefine services outside component to avoid recreation on each render
const services = [
  { name: "VitalCheck™", description: "Comprehensive health monitoring", url: "https://vitalcheck.flowcare.health/" },
  { name: "Companion+™", description: "Personalized companionship care", url: "https://companion.flowcare.health/" },
  { name: "Household Support", description: "Home maintenance assistance", url: "https://getstarted.flowcare.health" },
  { name: "ADL Swift™", description: "Activities of daily living support", url: "https://adls.flowcare.health/" },
  { name: "Transportation", description: "Safe and reliable transport", url: "https://vitalride.flowcare.health/" }
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Memoize the scroll handler to prevent unnecessary rerenders
  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 10);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToContact = useCallback(() => {
    const contactSection = document.getElementById('call-to-action');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
      if (mobileMenuOpen) setMobileMenuOpen(false);
    }
  }, [mobileMenuOpen]);

  const scrollToHowItWorks = useCallback(() => {
    const howItWorksSection = document.getElementById('fractional-nursing');
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ behavior: 'smooth' });
      if (mobileMenuOpen) setMobileMenuOpen(false);
    }
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      isScrolled ? "bg-white shadow-md py-2" : "bg-transparent py-4"
    )}>
      {/* Main Navigation */}
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between bg-white lg:bg-transparent">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <div className="h-10 w-10 mr-2">
            <img 
              src="/lovable-uploads/57181b01-191c-43da-86d9-a331350c2776.png" 
              alt="FlowCare Logo" 
              className="h-full w-full object-contain"
              loading="eager" // Prioritize logo loading
            />
          </div>
          <div className="flex flex-col">
            <span className="text-2xl font-bold text-abbella-ebony">Flow<span className="text-abbella-aqua">Care</span></span>
            <span className="text-xs text-slate-600">by Abbella</span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:block">
          <NavigationMenu>
            <NavigationMenuList className="gap-1">
              <NavigationMenuItem>
                <Link to="/about">
                  <NavigationMenuLink className="px-4 py-2 text-abbella-ebony hover:text-abbella-aqua transition-colors">
                    About Us
                  </NavigationMenuLink>
                </Link>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuLink 
                  className="px-4 py-2 text-abbella-ebony hover:text-abbella-aqua transition-colors cursor-pointer"
                  onClick={scrollToHowItWorks}
                >
                  How It Works
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="px-4 py-2 text-abbella-ebony hover:text-abbella-aqua transition-colors">
                  Our Services
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4">
                    {services.map((service) => (
                      <li key={service.name}>
                        <a 
                          href={service.url}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="text-sm font-medium leading-none">{service.name}</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            {service.description}
                          </p>
                        </a>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </nav>

        {/* CTA Button */}
        <div className="hidden md:block">
          <Button 
            onClick={scrollToContact}
            className="bg-abbella-aqua hover:bg-abbella-aqua/90 transition-all hover:scale-105"
          >
            Get Started
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden text-abbella-ebony"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className={cn(
          "fixed inset-0 bg-white z-40 pt-20 transition-transform duration-300 lg:hidden",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}>
          <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
            <Link to="/about" className="text-lg font-medium py-3 border-b border-gray-100">About Us</Link>
            <button onClick={scrollToHowItWorks} className="text-lg font-medium py-3 border-b border-gray-100 text-left">How It Works</button>
            
            <div className="py-3 border-b border-gray-100">
              <p className="text-lg font-medium mb-2">Our Services</p>
              <div className="pl-4 flex flex-col gap-2">
                {services.map((service) => (
                  <a key={service.name} href={service.url} className="text-md py-1">
                    {service.name}
                  </a>
                ))}
              </div>
            </div>
            
            <Button 
              className="bg-abbella-aqua hover:bg-abbella-aqua/90 mt-4"
              onClick={scrollToContact}
            >
              Get Started
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
